import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Mail, Lock, RefreshCcw, Loader } from "lucide-react";
import VRSOrbitLogo from "../../Assets/vrs orbit logo.png";
import avatar from "../../Assets/avatar.png";
import desk from "../../Assets/rafiki.png";
import leftBg from "../../Assets/left-bg.png";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { ForceLogout, OrbitLogin } from "../../Services/LoginService";
import { getCaptchaService } from "../../Services/ToolKitService";
import NotificationAlert from "../Alerts/NotificationAlert";
import logo from "../../Assets/VRSorbit logo 1.png";
import ThemeToggle from "../Common/ThemeToggle";

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [pageLoaded, setPageLoaded] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [forceLoading, setForceLoading] = useState(false);
  //  Alert state
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();

    // Check Email & Password Empty
    if (!email.trim() || !password.trim()) {
      setAlert({
        show: true,
        type: "warning",
        message: "Please enter both email and password.",
      });
      return;
    } else {
      // Password Policy Check
      const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

      if (!passwordRegex.test(password)) {
        setAlert({
          show: true,
          type: "error",
          message:
            "Password must be at least 8 characters long, include uppercase, lowercase, number, and special character.",
        });
        return;
      }
    }
    // Captcha Empty Check
    if (!captchaInput.trim()) {
      setAlert({
        show: true,
        type: "warning",
        message: "Please enter captcha.",
      });
      return;
    } else {
      // Captcha Check
      if (
        captchaInput.toUpperCase() === captchaText.toUpperCase() &&
        captchaInput !== captchaText
      ) {
        setAlert({
          show: true,
          type: "warning",
          message: "Captcha is case-sensitive. Please enter exactly as shown!",
        });

        getCaptcha();
        setCaptchaInput("");
        return;
      }

      if (captchaInput.toUpperCase() !== captchaText.toUpperCase()) {
        setAlert({
          show: true,
          type: "error",
          message: "Incorrect captcha. Please try again!",
        });

        getCaptcha();
        setCaptchaInput("");
        return;
      }
    }
    setLoading(true);

    try {
      const result = await OrbitLogin(email, password, keepMeLoggedIn);

      if (result?.status === "SessionLimit") {
        // setAlert({ show: true, type: "warning", message: result.message });
        localStorage.setItem(
          "pendingLogin",
          JSON.stringify({ email, password, keepMeLoggedIn }),
        );
        setSessions(result.sessions || []);
        setLoading(false);
        return;
      }

      if (result?.status === "Success") {
        const token = result.token?.token;

        if (token) {
          Cookies.set("Orbit_Token", token, {
            secure: true,
            sameSite: "Strict",
            expires: keepMeLoggedIn ? 30 : 1 / 3,
          });
        }

        setAlert({
          show: true,
          type: "success",
          message: result.message || "Login successful! Redirecting...",
        });

        setTimeout(() => navigate("/home"), 1000);
      } else {
        setAlert({
          show: true,
          type: "error",
          message: result.message || "Invalid credentials.",
        });
        getCaptcha();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setAlert({
          show: true,
          type: "error",
          message: error.response?.data?.message || "Invalid Credentials.",
        });
        return;
      }

      if (error.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: error.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }

      setAlert({
        show: true,
        type: "error",
        message: "Server error, please try again!",
      });
    } finally {
      setCaptchaInput("");
      setLoading(false);
    }
  };

  const getCaptcha = async () => {
    try {
      const response = await getCaptchaService();
      const data = response.data;

      setCaptchaImage(data.captchaImage);
      setCaptchaText(data.captchaText);
      setCaptchaInput("");
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      console.error(err);
      setAlert({
        show: true,
        type: "error",
        message: "Failed to load captcha. Try again!",
      });
    }
  };

  const handleForceLogout = async (slot) => {
    setForceLoading(true);
    try {
      const pending = JSON.parse(localStorage.getItem("pendingLogin"));
      if (pending) {
        const response = await ForceLogout(pending.email, slot);

        if (response.data?.status !== "Success") {
          setAlert({
            show: true,
            type: "error",
            message: response.data?.message || "alert failed",
          });
          return;
        }

        const result = await OrbitLogin(
          pending.email,
          pending.password,
          pending.keepMeLoggedIn,
        );
        if (result?.status === "Success") {
          localStorage.removeItem("pendingLogin");
        }
        const token = result.token?.token;
        if (token) {
          Cookies.set("Orbit_Token", token, {
            secure: true,
            sameSite: "Strict",
            expires: keepMeLoggedIn ? 30 : 1 / 3,
          });
        }
        setSessions([]);
        setAlert({ show: true, type: "success", message: "Login successful!" });
        setTimeout(() => navigate("/home"), 1000);
      }
    } catch (err) {
      setAlert({
        show: true,
        type: "error",
        message:
          err.response?.data?.message ||
          "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setForceLoading(false);
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return "";

    const past = new Date(dateString); // ✅ correct parsing
    if (isNaN(past.getTime())) return "Unknown time";

    const now = new Date();
    const diffMs = now - past;

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (minutes < 1) return "Last active just now";
    if (minutes < 60) return `Last active ${minutes} min ago`;
    if (hours < 24) return `Last active ${hours} hr ago`;
    return `Last active ${days} day(s) ago`;
  };

  useEffect(() => {
    getCaptcha();
  }, []);

  useEffect(() => {
    setTimeout(() => setPageLoaded(true), 100);
  }, []);

  return (
    <>
      <div className="min-h-screen flex flex-col md:flex-row relative overflow-hidden">
        <NotificationAlert
          show={alert.show}
          type={alert.type}
          message={alert.message}
          duration={1500}
          onClose={() => setAlert({ ...alert, show: false })}
        />

        {/* Left Side */}
        <div
          className={`hidden md:flex flex-col items-center justify-center w-1/2 text-white p-10 relative overflow-hidden transition-all duration-700 ease-out
         ${pageLoaded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}
         `}
          style={{
            backgroundImage: `url(${leftBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="relative z-10 text-center">
            <img
              src={avatar}
              alt="Avatar"
              className="mx-auto w-30 h-20 mb-[43px]"
            />
            <h1 className="text-3xl font-bold mb-2">WELCOME BACK!</h1>
            <p className="text-lg mb-10">
              To keep connected with us <br /> please login with your personal
              info
            </p>
            <img
              src={desk}
              alt="Desk Illustration"
              className="mx-auto w-[450px] mt-6"
            />
          </div>
        </div>

        {/* Right Side */}
        <div
          className={`flex flex-col justify-center items-center w-full md:w-1/2 bg-white px-4 py-8 sm:px-6 md:p-8 theme-login-panel
         transition-all duration-700 ease-out delay-150
         ${pageLoaded ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}
       `}
        >
          <div
            className={`max-w-md w-full transition-all duration-700 delay-300
          ${pageLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
          `}
          >
            <div className="mb-6 flex justify-end">
              <ThemeToggle />
            </div>

            {/* Logo */}
            <div className="flex items-center justify-center mb-10 sm:mb-16">
              <img
                src={VRSOrbitLogo}
                alt="VRS Orbit"
                className="mr-2 w-full max-w-[450px]"
              />
            </div>

            <h3 className="text-center text-base sm:text-lg font-semibold mb-8 sm:mb-10 theme-text-primary">
              Login in to your account to continue
            </h3>

            <form className="space-y-4" onSubmit={handleLogin}>
              {/* Email */}
              <label className="theme-text-primary">Email Address</label>
              <div className="relative">
                <Mail
                  className="absolute left-3 top-3 text-gray-400"
                  size={20}
                />
                <input
                  type="email"
                  placeholder="name@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 mb-6 sm:mb-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF] theme-form-input"
                />
              </div>

              {/* Password */}
              <label className="theme-text-primary">Password</label>
              <div className="relative">
                <Lock
                  className="absolute left-3 top-3 text-gray-400"
                  size={20}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full mb-6 pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF] theme-form-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-500 theme-text-secondary"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Captcha */}
              <label className="theme-text-primary">Captcha</label>
              <div className="flex flex-col items-stretch gap-3 mb-6 sm:flex-row sm:items-center">
                {/* Captcha Image */}
                <div
                  className="cursor-pointer rounded-md flex items-center justify-center shadow-sm"
                  onClick={getCaptcha}
                >
                  {captchaImage ? (
                    <img
                      src={captchaImage}
                      alt="Captcha"
                      className="h-[45px] w-auto select-none"
                      draggable="false"
                    />
                  ) : (
                    <div className="h-[45px] w-[120px] flex justify-center items-center">
                      <Loader
                        className="animate-spin text-gray-500"
                        size={22}
                      />
                    </div>
                  )}
                </div>

                {/* Refresh Icon */}
                <button
                  type="button"
                  onClick={getCaptcha}
                  title="Refresh Captcha"
                  className="text-[#0061A4] hover:text-[#004a80] transition p-1"
                >
                  <RefreshCcw size={22} />
                </button>

                {/* Captcha Input */}
                <input
                  type="text"
                  placeholder="Enter code"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  className="w-full sm:w-[200px] pl-3 pr-2 py-2 border border-gray-300 rounded-md theme-form-input
               focus:outline-none focus:ring-2 focus:ring-[#007BFF] text-sm"
                />
              </div>

              {/* Keep Me Logged In */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-2">
                <label className="flex items-center space-x-2 text-sm text-gray-700 theme-text-primary">
                  <input
                    type="checkbox"
                    checked={keepMeLoggedIn}
                    onChange={() => setKeepMeLoggedIn(!keepMeLoggedIn)}
                    className="accent-[#0061A4] cursor-pointer"
                  />
                  <span>Keep me logged in</span>
                </label>
                <button
                  type="button"
                  className="text-[#0061A4] text-sm hover:underline font-medium self-start sm:self-auto"
                >
                  Forgot your password?
                </button>
              </div>

              {/* Login Button */}
              <div className="flex justify-center mt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full max-w-[220px] ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-[#0061A4] to-[#00ACED]"
                  } text-white py-2 rounded-md font-medium hover:opacity-90 transition`}
                >
                  {loading ? "Loading..." : "Login"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {sessions.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-[scrimIn_220ms_ease]">
          <div
            className="bg-white w-[420px] max-w-[calc(100vw-24px)] rounded-[28px] shadow-2xl overflow-hidden relative theme-session-modal"
            style={{
              animation: "dialogIn 280ms cubic-bezier(0.34,1.56,0.64,1)",
            }}
          >
            {/* Header */}
            <div className="px-7 pt-7">
              <div className="flex justify-between">
                <div className="w-10 h-10 rounded-full bg-[#fce8e6] flex items-center justify-center mb-4">
                  {/* devices icon — swap for your icon lib */}
                  <svg
                    className="w-5 h-5 text-[#d93025]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M4 6h18V4H4c-1.1 0-2 .9-2 2v11H0v3h14v-3H4V6zm19 2h-6c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h6c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1zm-1 9h-4v-7h4v7z" />
                  </svg>
                </div>
                <img src={logo} alt="" className="w-9 h-9" />
              </div>
              <h2
                style={{ fontFamily: "'Google Sans', sans-serif" }}
                className="text-[22px] font-normal text-[#202124] leading-snug mb-2.5"
              >
                You're signed in on multiple devices
              </h2>
              <p className="text-sm text-[#5f6368] leading-relaxed">
                To continue, please sign out from one of the devices below.
              </p>
              <div className="h-px bg-[#e8eaed] mt-5" />
            </div>

            {/* Sessions */}
            <div className="max-h-[260px] overflow-y-auto px-3 py-2">
              {sessions
                .filter((s) => s.device || s.lasteSeen)
                .map((s) => (
                  <div
                    key={s.slot}
                    className="group flex items-center gap-3.5 px-4 py-3 rounded-xl hover:bg-[#f8f9fa] transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#e8f0fe] flex items-center justify-center shrink-0">
                      <svg
                        className="w-5 h-5 text-[#1a73e8]"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        style={{ fontFamily: "'Google Sans', sans-serif" }}
                        className="text-sm font-medium text-[#202124] truncate"
                      >
                        {s.device || "Unknown Device"}
                      </p>
                      <p className="text-xs text-[#80868b] mt-0.5">
                        {getTimeAgo(s.lasteSeen)}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={forceLoading}
                      onClick={() => handleForceLogout(s.slot)}
                      className="text-[#d93025] border border-[#d9302560] hover:border-[#d93025] hover:bg-[#fce8e6] text-[13px] font-medium rounded-full px-3.5 py-1.5 transition-all shrink-0"
                      style={{ fontFamily: "'Google Sans', sans-serif" }}
                    >
                      Sign out
                    </button>
                  </div>
                ))}
            </div>

            {/* Footer */}
            <div className="flex justify-end items-center px-5 py-4 border-t border-[#e8eaed]">
              <button
                onClick={() => setSessions([])}
                className="text-[#1a73e8] text-sm font-medium rounded-full px-4 py-2 hover:bg-[#e8f0fe] transition-colors"
                style={{ fontFamily: "'Google Sans', sans-serif" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoginPage;
